import React, { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { createPortalSession, syncSubscription } from '../utils/stripe'
import DonateModal from './DonateModal'
import { getApiBaseUrl } from '../utils/apiBaseUrl'

interface SubscriptionDetails {
  status: string
  cancel_at_period_end: boolean
  current_period_start: number
  current_period_end: number
  created: number
  /** Stripe Price.recurring.interval: "month" | "year" */
  price_interval?: string
  unit_amount_cents?: number
  price_currency?: string
  stripe_price_id?: string
}

interface SubscriptionStatus {
  has_subscription: boolean
  subscription_tier?: string
  stripe_customer_id: string
  stripe_subscription_id: string
  subscription_details?: SubscriptionDetails
}

const AccountSettings: React.FC = () => {
  const { user, isAuthenticated, loading: authLoading, refreshUser } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [canceling, setCanceling] = useState(false)
  const [cancelSuccess, setCancelSuccess] = useState(false)
  const [managingBilling, setManagingBilling] = useState(false)
  const [donationSuccess, setDonationSuccess] = useState(false)
  const [isDonateModalOpen, setIsDonateModalOpen] = useState(false)
  const [showCancelModal, setShowCancelModal] = useState(false)

  useEffect(() => {
    if (searchParams.get('donation') === 'success' && isAuthenticated) {
      setDonationSuccess(true)
      searchParams.delete('donation')
      searchParams.delete('session_id')
      setSearchParams(searchParams)
    }
    if (searchParams.get('donation') === 'cancelled') {
      searchParams.delete('donation')
      setSearchParams(searchParams)
    }
  }, [searchParams, isAuthenticated, setSearchParams])

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      fetchSubscriptionStatus()
    }
  }, [authLoading, isAuthenticated])

  const fetchSubscriptionStatus = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('auth_token')
      if (!token) {
        throw new Error('No authentication token found')
      }

      const API_BASE_URL = getApiBaseUrl()

      const response = await fetch(`${API_BASE_URL}/api/stripe/subscription-status`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        throw new Error('Failed to fetch subscription status')
      }

      const data = await response.json()
      setSubscriptionStatus(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setLoading(false)
    }
  }

  const handleCancelSubscription = async () => {
    try {
      setCanceling(true)
      const token = localStorage.getItem('auth_token')
      if (!token) {
        throw new Error('No authentication token found')
      }

      const API_BASE_URL = getApiBaseUrl()

      const response = await fetch(`${API_BASE_URL}/api/stripe/cancel-subscription`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to cancel subscription')
      }

      setCancelSuccess(true)
      setShowCancelModal(false)
      // Refresh subscription status
      await fetchSubscriptionStatus()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setCanceling(false)
    }
  }

  const handleManageBilling = async () => {
    try {
      setManagingBilling(true)
      setError(null)
      const token = localStorage.getItem('auth_token')
      if (!token) {
        throw new Error('Please sign in to continue')
      }
      const { url } = await createPortalSession(token)
      if (url) {
        window.location.href = url
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'An error occurred'
      setError(msg === 'Failed to fetch'
        ? 'Could not connect to server. Ensure the backend is running and REACT_APP_API_URL points to it.'
        : msg)
    } finally {
      setManagingBilling(false)
    }
  }

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  /** Label from Stripe price (source of truth), not tier guess — fixes annual shown as "/month". */
  const formatPlanPrice = (details: SubscriptionDetails | undefined, tier: string | undefined) => {
    const cents = details?.unit_amount_cents
    const interval = details?.price_interval
    if (cents != null && cents >= 0 && (interval === 'month' || interval === 'year')) {
      const dollars = (cents / 100).toFixed(2)
      return interval === 'year' ? `$${dollars}/year` : `$${dollars}/month`
    }
    if (tier === 'instant') return '$19.99/month'
    if (tier === 'basic') return '$4.99/month'
    return '$9.99/month'
  }

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Access Denied</h1>
          <p className="text-gray-600 dark:text-gray-400">Please sign in to view your account settings.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Account Settings</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">Manage your account — inventory access is free</p>
          </div>

          <div className="p-6 space-y-8">
            {/* Profile Information */}
            <div>
              <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Profile Information</h2>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Name</label>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">{user?.name || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email</label>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">{user?.email || 'N/A'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Site access */}
            <div>
              <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Site Access</h2>
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                <p className="text-sm text-green-800 dark:text-green-200">
                  You have full access to live BMW carbon bucket inventory while signed in. No subscription required.
                </p>
              </div>
            </div>

            {/* Support */}
            <div>
              <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Support the Project</h2>
              {donationSuccess && (
                <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <p className="text-green-800 dark:text-green-200 text-sm">
                    Thank you for your donation! It helps cover scraping, hosting, and improvements.
                  </p>
                </div>
              )}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Donations are optional and do not change your access. They help keep the site running and improving.
                </p>
                <button
                  onClick={() => setIsDonateModalOpen(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  Make a donation
                </button>
              </div>
            </div>

            {/* Legacy subscription (existing paying users) */}
            <div>
              <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Billing</h2>
              

              {cancelSuccess && (
                <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <p className="text-green-800 dark:text-green-200 text-sm">
                      Subscription canceled successfully. You'll continue to have access until the end of your billing period.
                    </p>
                  </div>
                </div>
              )}

              {error && (
                <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-red-800 dark:text-red-200 text-sm">{error}</p>
                  </div>
                </div>
              )}

              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mr-2"></div>
                    <span className="text-gray-600 dark:text-gray-400">Loading subscription status...</span>
                  </div>
                ) : subscriptionStatus ? (
                  <div className="space-y-4">
                    {/* Subscription Status */}
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Status</h3>
                        <div className="flex items-center mt-1">
                          {subscriptionStatus.has_subscription ? (
                            <>
                              <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                              <span className="text-sm text-green-600 dark:text-green-400 font-medium">Active Subscriber</span>
                            </>
                          ) : (
                            <>
                              <div className="w-2 h-2 bg-gray-400 rounded-full mr-2"></div>
                              <span className="text-sm text-gray-600 dark:text-gray-400">No Active Subscription</span>
                            </>
                          )}
                        </div>
                      </div>
                      
                      {subscriptionStatus.has_subscription && (
                        <div className="text-right">
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {subscriptionStatus.subscription_tier === 'instant' ? 'Instant' :
                             subscriptionStatus.subscription_tier === 'premium' ? 'Premium' :
                             subscriptionStatus.subscription_tier === 'basic' ? 'Basic' : 'Premium'} Plan
                          </p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {formatPlanPrice(subscriptionStatus.subscription_details, subscriptionStatus.subscription_tier)}
                          </p>
                          {subscriptionStatus.subscription_details?.price_interval && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                              Billed {subscriptionStatus.subscription_details.price_interval === 'year' ? 'annually' : 'monthly'}
                            </p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Subscription Details */}
                    {subscriptionStatus.subscription_details && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                        <div>
                          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                            Current billing period
                          </label>
                          <p className="mt-1 text-sm text-gray-900 dark:text-white">
                            {formatDate(subscriptionStatus.subscription_details.current_period_start)} - {formatDate(subscriptionStatus.subscription_details.current_period_end)}
                          </p>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                            {subscriptionStatus.subscription_details.cancel_at_period_end
                              ? 'Access until'
                              : 'Next renewal'}
                          </label>
                          <p className="mt-1 text-sm text-gray-900 dark:text-white">
                            {subscriptionStatus.subscription_details.cancel_at_period_end
                              ? formatDate(subscriptionStatus.subscription_details.current_period_end)
                              : formatDate(subscriptionStatus.subscription_details.current_period_end)}
                          </p>
                          {subscriptionStatus.subscription_details.cancel_at_period_end && (
                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                              You canceled renewal; prepaid access continues through this date (same as Stripe).
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Legacy subscription management */}
                    <div className="pt-4 border-t border-gray-200 dark:border-gray-600">
                      {subscriptionStatus.has_subscription ? (
                        <div className="space-y-3">
                          {subscriptionStatus.stripe_customer_id && (
                            <button
                              onClick={handleManageBilling}
                              disabled={managingBilling}
                              className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed mr-3"
                            >
                              {managingBilling ? (
                                <>
                                  <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mr-2"></div>
                                  Opening...
                                </>
                              ) : (
                                'Manage billing & change plan'
                              )}
                            </button>
                          )}
                          {subscriptionStatus.subscription_details?.cancel_at_period_end ? (
                            <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                                <strong>Subscription Canceled:</strong> You'll continue to have access until {formatDate(subscriptionStatus.subscription_details.current_period_end)}.
                              </p>
                            </div>
                          ) : (
                            <button
                              onClick={() => setShowCancelModal(true)}
                              disabled={canceling}
                              className="inline-flex items-center px-4 py-2 border border-red-300 dark:border-red-600 text-sm font-medium rounded-md text-red-700 dark:text-red-300 bg-white dark:bg-gray-800 hover:bg-red-50 dark:hover:bg-red-900/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Cancel Subscription
                            </button>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-600 dark:text-gray-400 py-2">
                          No active subscription on file. The site is free for all signed-in users.
                        </p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-600 dark:text-gray-400">Unable to load subscription information.</p>
                    <button
                      onClick={fetchSubscriptionStatus}
                      className="mt-2 text-blue-600 hover:text-blue-500 text-sm font-medium"
                    >
                      Try Again
                    </button>
                  </div>
                )}

                {/* Cancel Subscription Modal */}
                {showCancelModal && subscriptionStatus?.subscription_details && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowCancelModal(false)}>
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Cancel subscription?</h3>
                      <p className="text-gray-600 dark:text-gray-400 text-sm mb-6">
                        Are you sure you want to cancel your subscription? You will continue to have access until the end of your current billing period ({formatDate(subscriptionStatus.subscription_details.current_period_end)}).
                      </p>
                      <div className="flex justify-end gap-3">
                        <button
                          onClick={() => setShowCancelModal(false)}
                          className="px-4 py-2 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
                        >
                          Keep subscription
                        </button>
                        <button
                          onClick={handleCancelSubscription}
                          disabled={canceling}
                          className="px-4 py-2 text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                        >
                          {canceling ? 'Canceling...' : 'Yes, cancel'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {subscriptionStatus && (
                  <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                    Payment not reflecting?{' '}
                    <button
                      onClick={async () => {
                        try {
                          const token = localStorage.getItem('auth_token')
                          if (token) {
                            await syncSubscription(token)
                            if (refreshUser) await refreshUser()
                            await fetchSubscriptionStatus()
                          }
                        } catch (_) {}
                      }}
                      className="text-blue-600 hover:text-blue-500 underline"
                    >
                      Sync from Stripe
                    </button>
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <DonateModal isOpen={isDonateModalOpen} onClose={() => setIsDonateModalOpen(false)} />
    </div>
  )
}

export default AccountSettings
